import type { AssetIdentifier, FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import type { BigNumber } from "@opensea/utils"
import { bn } from "@opensea/utils"
import { ContextCurrencyFragment } from "./ContextCurrency.fragment"
import { CurrencyIdentifierFragment } from "./CurrencyIdentifier.fragment"
import type { Currency, CurrencyIdentifier } from "./types"

// Contract address is not enough to differentiate currencies.
// For example, native tokens on all chains will have same address (null)
// but could be very different (ETH vs SEI)
export const getCurrencyKey = (currency: CurrencyIdentifier): string => {
  return `${currency.chainIdentifier}-${currency.contractAddress}`
}

export const readCurrencyFragment = (
  currency: FragmentOf<typeof ContextCurrencyFragment>,
): Currency => {
  return readFragment(ContextCurrencyFragment, currency)
}

export const readCurrencyIdentifierFragment = (
  currencyIdentifier: FragmentOf<typeof CurrencyIdentifierFragment>,
): CurrencyIdentifier => {
  const data = readFragment(CurrencyIdentifierFragment, currencyIdentifier)
  return {
    contractAddress: data.contractAddress,
    chainIdentifier: data.chainIdentifier,
  }
}

export const currencyToAssetIdentifier = (
  currency: CurrencyIdentifier,
): AssetIdentifier => {
  return {
    chain: currency.chainIdentifier,
    contractAddress: currency.contractAddress,
  }
}

export const getConversionRate = (
  fromCurrency: Currency,
  toCurrency: Currency,
): BigNumber | undefined => {
  return bn(fromCurrency.usdPrice).dividedBy(toCurrency.usdPrice)
}

export const areCurrenciesEqual = (
  a: CurrencyIdentifier,
  b: CurrencyIdentifier,
): boolean => {
  return getCurrencyKey(a) === getCurrencyKey(b)
}
